
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectOverdueActions } from './redux/selectors/overdueActions';
import {
  Grid,
  Title,
  Text,
  Box,
  Table,
  Modal,
  Card,
  Paper,
  SegmentedControl,
  Tooltip as MantineTooltip,
  Pagination,
  Transition,
  Container,
  Stack,
  ThemeIcon,
  Group
} from '@mantine/core';
import {
  IconExclamationMark,
  IconAlarm,
  IconChartBar,
  IconTable,
  IconGavel,
  IconClipboardCheck
} from '@tabler/icons-react';
import SecurityReportData from './SecurityReportData';
import DynamicLabel from './DynamicLabel';
import { differenceInDays, parseISO, format } from 'date-fns';
import RisksBySource from './risksBySource';


import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  LabelList,
} from 'recharts';


// Consistent vertical spacing between dashboard sections
const SECTION_SPACING_PX = 32;

const CATEGORY_COLORS = [
  '#E3E3E3', // Light Gray
  '#B8C5D6', // Soft Blue Gray
  '#94A7BC', // Muted Blue
  '#7B8B99', // Steel Blue
  '#5D5D5D', // Medium Gray
  '#465362', // Dark Blue Gray
  '#D8E2DC', // Pale Green Gray
  '#E8E8E8', // Off White
  '#C4C4C4', // Silver Gray
  '#A5A5A5', // Medium Light Gray
  '#909090', // Neutral Gray
  '#BCB8A6', // Warm Gray
  '#9BA7B4', // Cool Gray
  '#8E9AAA', // Slate Gray
];

const COLORS = [
  '#94A7BC', // Muted Blue
  '#BCB8A6', // Warm Gray
  '#7B8B99', // Steel Blue
  '#D8E2DC', // Pale Green Gray
  '#B8C5D6', // Soft Blue Gray
  '#A5A5A5', // Medium Light Gray
  '#9BA7B4', // Cool Gray
  '#C4C4C4', // Silver Gray
  '#8E9AAA', // Slate Gray
  '#E3E3E3', // Light Gray
];

// Define risk priority colors consistent with AssessmentReport
const riskPriorityColors = {
  Critical: '#1A1A1A',    // Black
  High: '#993333',        // Red
  Significant: '#B94D4D', // Amber
  Moderate: '#D4AA7D',    // Light Orange
  Low: '#B8C2B7',         // Blue
  Negligible: '#D8D8D8',  // Green
  None: '#EBEBEB',        // Light Grey
};
const securityRiskColors = {
  Confidentiality: '#223B66',  // Deep, pronounced blue
  Integrity: '#B03E3A',        // Bold, pronounced red
  Availability: '#3F8E3F',     // Strong, pronounced green
};

// Helper function to convert camelCase to Title Case and normalize
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .trim() // Trim whitespace
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (char) => char.toUpperCase()) // Capitalize first character
    .trim();
};

const DashboardCard = ({ title, count, icon: Icon, children }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Card
        shadow="sm"
        padding="xl"
        radius="md"
        withBorder
        sx={{
          height: 180,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: '1px solid #e3e3e3',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }
        }}
        onClick={handleOpen}
      >
        <Stack align="center" justify="center" gap="lg" h="100%">
          <Group gap="sm">
            <ThemeIcon size={40} radius="xl" color="gray" variant="light">
              <Icon size={20} />
            </ThemeIcon>
            <Text size="md" fw={500}>
              {title}
            </Text>
          </Group>
          <Title order={2} fw={600} fz="2.5rem">
            {count}
          </Title>
        </Stack>
      </Card>
      <Modal
        opened={open}
        onClose={handleClose}
        centered
        size="lg"
        overflow="inside"
        styles={{
          body: {
            backgroundColor: '#f7f9fc',
            maxHeight: '90vh'
          }
        }}
      >
        {children || <Text>No details available.</Text>}
      </Modal>
    </>
  );
};

const RiskLandscape = () => {
  // RTK-first: Get data from RTK slices with fallback to legacy
  const legacyModels = useSelector((state) => state.risks.byAssessment || {});
  const assessmentsById = useSelector((state) => state.assessments?.byId || {});
  const controlsState = useSelector((state) => state.controls);
  // RTK riskItems slice - where autopilot saves risks
  const riskItemsByAssessment = useSelector((state) => state.riskItems?.byAssessment || {});

  // Merge RTK assessments with legacy models for comprehensive view
  // RTK assessments have: model_name, model_type, review_status, info.status, riskFormData (if populated)
  // Legacy models have: modelName, riskFormData, assessmentStatus, review_status
  // RTK riskItems have: byAssessment[assessmentUuid][areaName].areaRisks
  const models = useMemo(() => {
    const merged = { ...legacyModels };

    // Add/override with RTK assessment data
    Object.entries(assessmentsById).forEach(([assessmentUuid, assessment]) => {
      const modelId = assessment.model_uuid;
      if (!modelId) return;

      // Get risk data from RTK riskItems slice (autopilot flow stores risks here)
      const rtkRiskData = riskItemsByAssessment[assessmentUuid] || {};
      console.log('[RiskLandscape] RTK risk data for', assessmentUuid, ':', rtkRiskData);

      // Transform RTK riskItems format to match legacy modelRisks format
      // RTK: { modelRisks: { [section]: { areaRisks: { [category]: [risks] } } } }
      // Legacy: { [areaName]: { sectionRisks/areaRisks: { [sourceName]: [risks] } } }
      const rtkModelRisks = {};
      // Iterate over modelRisks object, not top level
      const modelRisksData = rtkRiskData.modelRisks || {};
      console.log('[RiskLandscape] Model risks data:', modelRisksData, 'keys:', Object.keys(modelRisksData));
      Object.entries(modelRisksData).forEach(([areaName, areaData]) => {
        if (areaData?.areaRisks) {
          rtkModelRisks[areaName] = {
            areaRisks: areaData.areaRisks,
            sectionRisks: areaData.areaRisks // Alias for compatibility
          };
        }
      });
      console.log('[RiskLandscape] Transformed RTK model risks:', rtkModelRisks);

      // If model doesn't exist in legacy, create it from RTK data
      if (!merged[modelId]) {
        merged[modelId] = {
          model_uuid: modelId,
          modelName: assessment.model_name,
          model_name: assessment.model_name,
          modelType: assessment.model_type,
          model_type: assessment.model_type,
          assessmentUuid: assessment.info?.assessment_uuid || assessmentUuid,
          assessmentStatus: assessment.info?.status,
          review_status: assessment.review_status,
          submitted_for_review_at: assessment.submitted_for_review_at,
          owner_name: assessment.owner_name,
          owner_email: assessment.owner_email,
          last_activity_by_name: assessment.info?.last_activity_by_name,
          last_activity_by_email: assessment.info?.last_activity_by_email,
          updated_at: assessment.info?.updated_at,
          // Prefer RTK riskItems, then assessment.riskFormData, then empty
          modelRisks: Object.keys(rtkModelRisks).length > 0
            ? rtkModelRisks
            : (assessment.modelRisks || assessment.riskFormData || {}),
        };
      } else {
        // Model exists in legacy - supplement with RTK data if missing
        // If legacy has no risks but RTK does, use RTK risks
        const existingRisks = merged[modelId].modelRisks || merged[modelId].riskFormData || {};
        const hasExistingRisks = Object.keys(existingRisks).some(area => {
          const areaData = existingRisks[area];
          const risks = areaData?.areaRisks || areaData?.sectionRisks || {};
          return Object.values(risks).some(arr => Array.isArray(arr) && arr.length > 0);
        });

        merged[modelId] = {
          ...merged[modelId],
          review_status: merged[modelId].review_status || assessment.review_status,
          assessmentStatus: merged[modelId].assessmentStatus || assessment.info?.status,
          // Use RTK risks if legacy has none
          modelRisks: hasExistingRisks ? existingRisks : (
            Object.keys(rtkModelRisks).length > 0 ? rtkModelRisks : existingRisks
          ),
        };
      }
    });

    return merged;
  }, [legacyModels, assessmentsById, riskItemsByAssessment]);

  // Use the SecurityReportData component
  const { dataByModel, dataByCategory } = SecurityReportData();

  // DEBUG: Log merged data sources
  console.log('[RiskLandscape] 📊 Data sources:', {
    mergedModels: Object.keys(models).length,
    rtkAssessments: Object.keys(assessmentsById).length,
    rtkRiskItems: Object.keys(riskItemsByAssessment).length,
    legacyModels: Object.keys(legacyModels).length
  });

  // Log which models have actual risk data
  Object.entries(models).forEach(([modelId, model]) => {
    const risks = model.modelRisks || model.riskFormData || {};
    const riskCount = Object.values(risks).reduce((count, area) => {
      const areaRisks = area?.areaRisks || area?.sectionRisks || {};
      return count + Object.values(areaRisks).reduce((c, arr) => c + (Array.isArray(arr) ? arr.length : 0), 0);
    }, 0);
    if (riskCount > 0) {
      console.log(`[RiskLandscape] ✅ Model ${model.modelName}: ${riskCount} risks found`);
    }
  });


  const overdueActions = useSelector(selectOverdueActions);

  // Assessments pending review (review_status === 'submitted')
  const assessmentsPendingReview = useMemo(() => {
    const pendingList = [];

    Object.values(models).forEach((model) => {
      if (model.review_status === 'submitted') {
        pendingList.push({
          modelId: model.model_uuid,
          modelName: model.modelName || model.model_name || 'Unnamed Model',
          requesterName: model.last_activity_by_name || model.owner_name || 'Unknown',
          requesterEmail: model.last_activity_by_email || model.owner_email || '',
          submittedAt: model.submitted_for_review_at || model.updated_at,
        });
      }
    });

    // Sort by submission date (newest first)
    return pendingList.sort((a, b) => {
      const dateA = a.submittedAt ? new Date(a.submittedAt) : new Date(0);
      const dateB = b.submittedAt ? new Date(b.submittedAt) : new Date(0);
      return dateB - dateA;
    });
  }, [models]);

const [openOwnerModal, setOpenOwnerModal] = useState(false);
const [selectedOwnerRisks, setSelectedOwnerRisks] = useState([]);
const [selectedOwnerName, setSelectedOwnerName] = useState('');


const [sourceView, setSourceView] = useState('chart');
const handleSourceViewChange = (event, newView) => {
  if (newView) setSourceView(newView);
};


const riskCounts = useMemo(() => {
  const counts = {
    Critical: 0,
    High: 0,
    Significant: 0,
    Moderate: 0,
    Low: 0,
    Negligible: 0,
    None: 0,
  };

  // Aggregate risks from standard models
  Object.values(models).forEach((model) => {
    Object.entries(model.modelRisks || model.riskFormData || {}).forEach(([sectionName, sectionData]) => {
      Object.entries(sectionData.areaRisks || sectionData.sectionRisks || {}).forEach(([category, risks]) => {
        if (Array.isArray(risks)) {
          risks.forEach((risk) => {
            const priority = toTitleCase((risk.priority || 'none').trim()); // Updated line
            if (counts.hasOwnProperty(priority)) {
              counts[priority] += 1;
            } else {
              // Handle unexpected priority by assigning to 'None'
              counts['None'] += 1;
              console.warn(`Unexpected priority "${priority}" encountered. Assigned to 'None'.`);
            }
          });
        }
      });
    });
  });

  // Aggregate security risks
  Object.entries(dataByModel).forEach(([modelUuid, modelData]) => {
    modelData.risks.forEach((risk) => {
      const priority = toTitleCase((risk.threat_level || 'none').trim()); // Updated line
      if (counts.hasOwnProperty(priority)) {
        counts[priority] += 1;
      } else {
        // Handle unexpected priority by assigning to 'None'
        counts['None'] += 1;
        console.warn(`Unexpected threat level "${priority}" encountered. Assigned to 'None'.`);
      }
    });
  });

  return counts;
}, [models, dataByModel]);
  // Transform riskCounts into an array suitable for BarChart
  const riskPriorityData = useMemo(() => {
    return Object.keys(riskCounts).map((key) => ({
      name: toTitleCase(key),
      count: riskCounts[key],
      risks: [], // Initialize an array to store risk names
    }));
  }, [riskCounts]);

  // Populate the risks for each priority
  useMemo(() => {
    // Clear existing risks
    riskPriorityData.forEach((data) => {
      data.risks = [];
    });

    // Add Model Risks
    Object.values(models).forEach((model) => {
      Object.entries(model.modelRisks || model.riskFormData || {}).forEach(([sectionName, sectionData]) => {
        Object.entries(sectionData.areaRisks || sectionData.sectionRisks || {}).forEach(([category, risks]) => {
          if (Array.isArray(risks)) {
            risks.forEach((risk) => {
              const priority = toTitleCase(risk.priority || 'none');
              const riskName = risk.riskName || 'Unnamed Risk';
              const priorityData = riskPriorityData.find((data) => data.name === priority);
              if (priorityData) {
                priorityData.risks.push(riskName);
              } else {
                // Assign to 'None' if priority not found
                const noneData = riskPriorityData.find((data) => data.name === 'None');
                if (noneData) {
                  noneData.risks.push(riskName);
                }
              }
            });
          }
        });
      });
    });

    // Add Security Risks
    Object.entries(dataByModel).forEach(([modelUuid, modelData]) => {
      //const modelName = `Security Model ${modelUuid}`; // Placeholder for model name
      modelData.risks.forEach((risk) => {
        const priority = toTitleCase(risk.threat_level || 'none');
        const riskName = risk.risk_name || 'Unnamed Security Risk';
        const priorityData = riskPriorityData.find((data) => data.name === priority);
        if (priorityData) {
          priorityData.risks.push(riskName);
        } else {
          // Assign to 'None' if priority not found
          const noneData = riskPriorityData.find((data) => data.name === 'None');
          if (noneData) {
            noneData.risks.push(riskName);
          }
        }
      });
    });
  }, [models, dataByModel, riskPriorityData]);

  const assessmentStatusCounts = useMemo(() => {
    const counts = { Completed: 0, 'On-Going': 0, 'Not Started': 0 };

    Object.values(models).forEach((model) => {
      if (model.assessmentStatus === 'Completed') counts.Completed += 1;
      else if (model.assessmentStatus === 'On-Going') counts['On-Going'] += 1;
      else counts['Not Started'] += 1;
    });

    return counts;
  }, [models]);

  // Aggregate risks by source and category
  const risksBySource = useMemo(() => {
    const sourceData = {};

    // Process standard models
    Object.values(models).forEach((model) => {
      Object.entries(model.modelRisks || model.riskFormData || {}).forEach(([source, sectionData]) => {
        Object.entries(sectionData.areaRisks || sectionData.sectionRisks || {}).forEach(([category, risks]) => {
          if (!sourceData[source]) {
            sourceData[source] = {};
          }
          const sanitizedCategory = toTitleCase(category.toLowerCase());
          sourceData[source][sanitizedCategory] = (sourceData[source][sanitizedCategory] || 0) + (Array.isArray(risks) ? risks.length : 0);
        });
      });
    });

    // Process security models using data from the custom hook
    Object.entries(dataByModel).forEach(([modelUuid, modelDetails]) => {
      modelDetails.risks.forEach((risk) => {
        const source = 'Security'; // Assuming all security risks belong to 'Security' source
        const category = risk.category
          ? toTitleCase(risk.category.trim().toLowerCase())
          : 'Uncategorized';
        if (!sourceData[source]) {
          sourceData[source] = {};
        }
        sourceData[source][category] = (sourceData[source][category] || 0) + 1;
      });
    });

    // Transform into array suitable for Recharts
    const chartData = Object.entries(sourceData).map(([source, categories]) => {
      return {
        source: toTitleCase(source),
        ...categories,
      };
    });

    return chartData;
  }, [models, dataByModel]);

  // Aggregate security risks by CIA triad for pie chart
  const securityRisksByCIA = useMemo(() => {
    const ciaMap = {
      Confidentiality: 0,
      Integrity: 0,
      Availability: 0
    };

    // Aggregate risks from dataByCategory (which now has CIA categories)
    Object.entries(dataByCategory).forEach(([category, risks]) => {
      const sanitizedCategory = toTitleCase(category.toLowerCase());
      if (ciaMap.hasOwnProperty(sanitizedCategory)) {
        ciaMap[sanitizedCategory] += Array.isArray(risks) ? risks.length : 0;
      }
    });

    // Transform into array suitable for PieChart
    return Object.entries(ciaMap)
      .filter(([, value]) => value > 0) // Only include categories with risks
      .map(([name, value]) => ({
        name,
        value,
      }));
  }, [dataByCategory]);

  // Aggregate risk priority distribution for bar chart
  // Uses riskCounts from the main models data (always available on the dashboard)
  const riskPriorityDistribution = useMemo(() => {
    const priorityOrder = ['Critical', 'High', 'Significant', 'Moderate', 'Low', 'Negligible'];
    return priorityOrder
      .map((name) => ({ name, value: riskCounts[name] || 0 }))
      .filter((entry) => entry.value > 0 || ['Critical', 'High', 'Moderate', 'Low'].includes(entry.name));
  }, [riskCounts]);

  // Aggregate policy coverage data across all models
  const policyCoverageData = useMemo(() => {
    console.log('[RiskLandscape] Computing policy coverage...');
    console.log('[RiskLandscape] controlsState:', controlsState);
    console.log('[RiskLandscape] controlsState.savedControls:', controlsState?.savedControls);
    console.log('[RiskLandscape] models count:', Object.keys(models).length);

    let totalControls = 0;
    let controlsWithEvidence = 0;
    const coverageByCIA = {
      Confidentiality: { total: 0, withEvidence: 0 },
      Integrity: { total: 0, withEvidence: 0 },
      Availability: { total: 0, withEvidence: 0 }
    };

    // NOTE: savedControls is structured as { [riskId]: [controls] }, not { [modelId]: {...} }
    // Iterate through all saved controls across all risks
    Object.entries(controlsState?.savedControls || {}).forEach(([riskId, controlsArray]) => {
      if (!Array.isArray(controlsArray)) return;

      console.log(`[RiskLandscape] Processing risk ${riskId}, controls count:`, controlsArray.length);

      controlsArray.forEach((control) => {
        totalControls++;
        const hasPolicyEvidence = control.policyEvidence && control.policyEvidence.length > 0;

        console.log(`[RiskLandscape] Control: ${control.name?.substring(0, 30)}, hasPolicyEvidence: ${hasPolicyEvidence}`);

        if (hasPolicyEvidence) {
          controlsWithEvidence++;
        }

        // Try to categorize by CIA based on risk data from SecurityReportData
        // Need to find which model this control belongs to, then find the risk
        const controlModelId = control.modelId || control.modelUuid;

        if (controlModelId && dataByModel[controlModelId]) {
          const securityModelData = dataByModel[controlModelId];
          if (securityModelData && securityModelData.risks) {
            const associatedRisk = securityModelData.risks.find(r =>
              control.riskId === r.risk_id || control.riskId === r.scenario_id
            );

            if (associatedRisk && associatedRisk.category) {
              const ciaCategory = associatedRisk.category;
              if (coverageByCIA[ciaCategory]) {
                coverageByCIA[ciaCategory].total++;
                if (hasPolicyEvidence) {
                  coverageByCIA[ciaCategory].withEvidence++;
                }
              }
            }
          }
        }
      });
    });

    const coveragePercentage = totalControls > 0
      ? Math.round((controlsWithEvidence / totalControls) * 100)
      : 0;

    const result = {
      total: totalControls,
      withEvidence: controlsWithEvidence,
      withoutEvidence: totalControls - controlsWithEvidence,
      coveragePercentage,
      byCIA: Object.entries(coverageByCIA).map(([name, data]) => ({
        name,
        total: data.total,
        withEvidence: data.withEvidence,
        coverage: data.total > 0 ? Math.round((data.withEvidence / data.total) * 100) : 0
      }))
    };

    console.log('[RiskLandscape] Policy coverage result:', result);

    return result;
  }, [models, controlsState, dataByModel]);
const uniqueCategories = useMemo(() => {
  const categorySet = new Set();

  // Categories from risksBySource data
  risksBySource.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'source' && key !== 'total') {
        categorySet.add(key);
      }
    });
  });

  // Categories from securityRisksByCIA data
  securityRisksByCIA.forEach((item) => {
    categorySet.add(item.name);
  });

  // Convert Set to Array and sort
  return Array.from(categorySet).sort();
}, [risksBySource, securityRisksByCIA]);

// Map each unique category to a color
const categoryColorMap = useMemo(() => {
  return uniqueCategories.reduce((acc, category, index) => {
    acc[category] = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
    return acc;
  }, {});
}, [uniqueCategories]);



  // Extract critical risks for the Critical Risks Table
  const criticalRisks = useMemo(() => {
    const criticalList = [];

    // Process standard models
    Object.values(models).forEach((model) => {
      const modelName = model.modelName; // Adjust based on actual model properties
      Object.entries(model.modelRisks || model.riskFormData || {}).forEach(([sectionName, sectionData]) => {
        Object.entries(sectionData.areaRisks || sectionData.sectionRisks || {}).forEach(([category, risks]) => {
          if (Array.isArray(risks)) {
            risks.forEach((risk) => {
              if (risk.priority && risk.priority.toLowerCase() === 'critical') {
                criticalList.push({
                  id: risk.riskId || 'N/A',
                  name: risk.riskName,
                  modelName, // Added modelName
                  category: toTitleCase(category),
                  source: toTitleCase(sectionName),
                });
              }
            });
          }
        });
      });
    });

    // Process security models
    Object.entries(dataByModel).forEach(([modelUuid, modelData]) => {
      const model = models[modelUuid]; // Retrieve the model using modelUuid
      const modelName = model?.modelName; 
      modelData.risks.forEach((risk) => {
        if (risk.threat_level && risk.threat_level.toLowerCase() === 'critical') {
          criticalList.push({
            id: risk.id || 'N/A',
            name: risk.risk_name || 'Unnamed Security Risk',
            modelName, // Added modelName
            category: risk.category ? toTitleCase(risk.category.trim().toLowerCase()) : 'Uncategorized',
            source: 'Security',
          });
        }
      });
    });

    return criticalList;
  }, [models, dataByModel]);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5); // Default rows per page (fixed, no need for setter)

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Slice criticalRisks based on pagination
  const paginatedCriticalRisks = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return criticalRisks.slice(start, end);
  }, [criticalRisks, page, rowsPerPage]);
  
  // Prepare data for the Assessment Status Pie Chart
  const assessmentStatusData = useMemo(() => [
    { name: 'Completed', value: assessmentStatusCounts.Completed },
    { name: 'On-Going', value: assessmentStatusCounts['On-Going'] },
    { name: 'Not Started', value: assessmentStatusCounts['Not Started'] },
  ], [assessmentStatusCounts]);

  const risksByOwnerData = useMemo(() => {
    const ownerCounts = {};
  
    // Process standard models
    Object.values(models).forEach((model) => {
      Object.entries(model.modelRisks || model.riskFormData || {}).forEach(([sectionName, sectionData]) => {
        Object.entries(sectionData.areaRisks || sectionData.sectionRisks || {}).forEach(([category, risks]) => {
          if (Array.isArray(risks)) {
            risks.forEach((risk) => {
              const ownerName = risk.owner ? risk.owner.name : 'Unassigned';
              if (!ownerCounts[ownerName]) {
                ownerCounts[ownerName] = { count: 0, risks: [] };
              }
              ownerCounts[ownerName].count += 1;
              ownerCounts[ownerName].risks.push(risk.riskName || 'Unnamed Risk');
            });
          }
        });
      });
    });

  
    // Process security risks
    Object.entries(dataByModel).forEach(([modelUuid, modelData]) => {
      modelData.risks.forEach((risk) => {
        const ownerName = risk.owner_name || 'Unassigned';
        if (!ownerCounts[ownerName]) {
          ownerCounts[ownerName] = { count: 0, risks: [] };
        }
        ownerCounts[ownerName].count += 1;
        ownerCounts[ownerName].risks.push(risk.risk_name || 'Unnamed Security Risk');
      });
    });
  
    return Object.keys(ownerCounts).map((ownerName) => ({
      name: ownerName,
      count: ownerCounts[ownerName].count,
      risks: ownerCounts[ownerName].risks,
    }));
  }, [models, dataByModel]);


  const handleOwnerBarClick = (data) => {
    if (data && data.risks) {
      setSelectedOwnerRisks(data.risks);
      setSelectedOwnerName(data.name);
      setOpenOwnerModal(true);
    }
  };
  
  const handleCloseOwnerModal = () => {
    setOpenOwnerModal(false);
  };
  const CustomOwnerBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2 }}>
          <Text size="lg">{`Risks owned by ${label}`}</Text>
          <ul>
            {Array.isArray(data.risks) && data.risks.length > 0 ? (
              data.risks.map((riskName, index) => (
                <li key={index}>{riskName}</li>
              ))
            ) : (
              <li>No risks available.</li>
            )}
          </ul>
        </Paper>
      );
    }
    return null;
  };

  // Prepare data for the Risks by Priority Pie Chart
  const risksByPriorityData = useMemo(() => riskPriorityData.map((item) => ({
    name: item.name,
    value: item.count,
  })), [riskPriorityData]);

  // Custom Tooltip Component for PieChart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1 }}>
          <Text weight={500}>{data.name}</Text>
          <Text size="sm">Count: {data.value}</Text>
        </Paper>
      );
    }
    return null;
  };

  // Custom Tooltip Component for BarChart
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1 }}>
          <Text weight={500}>{label}</Text>
          {payload.map((entry, index) => (
            <Text key={index} size="sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </Text>
          ))}
        </Paper>
      );
    }
    return null;
  };

  const calculateDaysOverdue = (dueDate) => {
    if (!dueDate) return null;
  
    const dueDateParsed = parseISO(dueDate);
    const now = new Date();
  
    const diffDays = differenceInDays(now, dueDateParsed);
  
    return diffDays > 0 ? diffDays : 0;
  };
  
  const complianceGaps = [];
  const complianceGapsCount = complianceGaps.length;


return (
  <Container size="100%" px="xl" sx={{ maxWidth: '1400px', margin: '0 auto' }}>
    <Stack gap={SECTION_SPACING_PX} mt={16}>
      <Grid gutter={24}>
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <DashboardCard title="Pending Review" count={assessmentsPendingReview.length} icon={IconClipboardCheck}>
            {assessmentsPendingReview.length > 0 ? (
              <Table fontSize="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Model Name</Table.Th>
                    <Table.Th>Requested By</Table.Th>
                    <Table.Th>Timestamp</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {assessmentsPendingReview.map((assessment, index) => (
                    <Table.Tr key={`${assessment.modelId}-${index}`}>
                      <Table.Td>{assessment.modelName}</Table.Td>
                      <Table.Td>
                        {assessment.requesterName}
                        {assessment.requesterEmail && (
                          <Text size="xs" c="dimmed">
                            {assessment.requesterEmail}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {assessment.submittedAt
                          ? format(parseISO(assessment.submittedAt), 'MMM d, yyyy h:mm a')
                          : 'N/A'}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text size="sm">No assessments pending review.</Text>
            )}
          </DashboardCard>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 3 }}>
          <DashboardCard title="Critical Risks" count={criticalRisks.length} icon={IconExclamationMark}>
          {criticalRisks.length > 0 ? (
            <>
              <Table fontSize="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Model Name</Table.Th>
                    <Table.Th>Category</Table.Th>
                    <Table.Th>Source</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginatedCriticalRisks.map((risk, index) => (
                    <Table.Tr key={`${risk.id}-${index}`}>
                      <Table.Td>{risk.name}</Table.Td>
                      <Table.Td>{risk.modelName}</Table.Td>
                      <Table.Td>{risk.category}</Table.Td>
                      <Table.Td>{risk.source}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Pagination
                  total={Math.ceil(criticalRisks.length / rowsPerPage)}
                  page={page + 1}
                  onChange={(newPage) => handleChangePage(null, newPage - 1)}
                  size="sm"
                />
                <Text size="sm">
                  Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, criticalRisks.length)} of {criticalRisks.length}
                </Text>
              </Box>
            </>
          ) : (
            <Text size="sm">No critical risks found.</Text>
          )}
        </DashboardCard>
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 3 }}>
        <DashboardCard title="Overdue Actions" count={overdueActions.length} icon={IconAlarm}>
          {overdueActions.length > 0 ? (
            <Table fontSize="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Control Name</Table.Th>
                  <Table.Th>Risk Name</Table.Th>
                  <Table.Th>Days Overdue</Table.Th>
                  <Table.Th>Model Name</Table.Th>
                  <Table.Th>Owner</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {overdueActions.map((action) => {
                  const daysOverdue = calculateDaysOverdue(action.dueDate);
                  return (
                    <Table.Tr key={action.id}>
                      <Table.Td>{action.controlName}</Table.Td>
                      <Table.Td>{action.riskName}</Table.Td>
                      <Table.Td>{daysOverdue} days</Table.Td>
                      <Table.Td>{action.modelName}</Table.Td>
                      <Table.Td>{action.owner}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          ) : (
            <Text size="sm">No overdue actions.</Text>
          )}
        </DashboardCard>
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 3 }}>
        <DashboardCard title="Compliance Gaps" count={complianceGapsCount} icon={IconGavel}>
          {complianceGapsCount > 0 ? (
            <Text size="sm">Compliance gaps details...</Text>
          ) : (
            <Text size="sm">No compliance gaps found.</Text>
          )}
        </DashboardCard>
      </Grid.Col>
    </Grid>

      {/* Risks by Source Section */}
      <Card
        shadow="sm"
        padding="xl"
        radius="md"
        withBorder
        sx={{ border: '1px solid #e3e3e3' }}
      >
      <Grid align="center" gutter={0} sx={{ marginBottom: 16 }}>
        <Grid.Col span="auto">
          <Title order={3} sx={{ fontWeight: 500, color: '#333' }}>
            Risks by Source
          </Title>
        </Grid.Col>
        <Grid.Col span="content">
          <SegmentedControl
            value={sourceView}
            onChange={(value) => handleSourceViewChange(null, value)}
            size="sm"
            data={[
              {
                value: 'chart',
                label: (
                  <MantineTooltip label="Chart view">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconChartBar size={18} />
                    </Box>
                  </MantineTooltip>
                )
              },
              {
                value: 'table',
                label: (
                  <MantineTooltip label="Table view">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconTable size={18} />
                    </Box>
                  </MantineTooltip>
                )
              }
            ]}
          />
        </Grid.Col>
      </Grid>
      <Transition mounted={sourceView === 'chart'} transition="fade" duration={300} timingFunction="ease">
        {(styles) => (
          <Box style={styles} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 24, marginTop: 16 }}>
          {/* existing chart Box code here */}
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                layout="vertical"
                data={risksBySource}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                barGap={0}
                barSize={32}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e0e0e0"
                  vertical={false}
                />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={{ stroke: '#d0d0d0' }}
                  tick={{ fontSize: 12, fill: '#555' }}
                />
                <YAxis
                  dataKey="source"
                  type="category"
                  width={140}
                  tick={{
                    fontSize: 14,
                    fontWeight: 500,
                    fill: '#333',
                    textAnchor: 'end',
                  }}
                  tickLine={false}
                  axisLine={{ stroke: '#d0d0d0' }}
                  tickFormatter={(value) => value.split('-')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div style={{
                          backgroundColor: '#fff',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <p style={{
                            margin: 0,
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '5px'
                          }}>{label.split('-')
                             .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                             .join(' ')}</p>
                          {payload.map((entry, index) => {
                            if (entry.value > 0) {
                              return (
                                <p key={`item-${index}`} style={{
                                  margin: 0,
                                  color: '#333',
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}>
                                  <span style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: entry.color,
                                    marginRight: '5px',
                                    borderRadius: '2px'
                                  }}></span>
                                  <span style={{ marginRight: '10px' }}>
                                    {entry.dataKey.split('-')
                                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                      .join(' ')}:
                                  </span>
                                  <span style={{ fontWeight: 'bold' }}>{entry.value}</span>
                                </p>
                              );
                            }
                            return null;
                          })}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {uniqueCategories.map((category, index) => (
                  <Bar
                    key={category}
                    dataKey={category}
                    stackId="a"
                    fill={categoryColorMap[category]}
                    animationDuration={600}
                    animationBegin={index * 100}
                    animationEasing="ease-out"
                  >
                    <LabelList
                      content={(props) => (
                        <DynamicLabel {...props} categoryName={category} />
                      )}
                    />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        )}
      </Transition>
      <Transition mounted={sourceView === 'table'} transition="fade" duration={300} timingFunction="ease">
        {(styles) => (
          <Box style={styles} sx={{ marginTop: 16 }}>
            <RisksBySource />
          </Box>
        )}
      </Transition>
    </Card>

      {/* Row 3: Risks by Priority and Risks by Owner */}
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            shadow="sm"
            padding="xl"
            radius="md"
            withBorder
            sx={{ height: '100%', border: '1px solid #e3e3e3' }}
          >
            <Title order={3} mb="md" sx={{ fontWeight: 500, color: '#333' }}>
              Risks by Priority
            </Title>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart
                data={risksByPriorityData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
                <XAxis
                  dataKey="name"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tickLine={false}
                  axisLine={{ stroke: '#d0d0d0' }}
                />
                <YAxis
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={{ stroke: '#d0d0d0' }}
                />
                <Tooltip content={<CustomBarTooltip />} />
                <Bar dataKey="value">
                  {risksByPriorityData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={riskPriorityColors[entry.name] || '#8884d8'}
                    />
                  ))}
                  <LabelList dataKey="value" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            shadow="sm"
            padding="xl"
            radius="md"
            withBorder
            sx={{ height: '100%', border: '1px solid #e3e3e3' }}
          >
            <Title order={3} mb="md" sx={{ fontWeight: 500, color: '#333' }}>
              Risks by Owner
            </Title>
            {risksByOwnerData && risksByOwnerData.length > 0 ? (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart
                  data={risksByOwnerData}
                  onClick={(data) => {
                    if (data && data.activePayload && data.activePayload.length > 0) {
                      handleOwnerBarClick(data.activePayload[0].payload);
                    }
                  }}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={70}
                    tickLine={false}
                    axisLine={{ stroke: '#d0d0d0' }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={{ stroke: '#d0d0d0' }}
                  />
                  <Tooltip content={<CustomOwnerBarTooltip />} />
                  <Bar dataKey="count" fill="#9BA7B4">
                    <LabelList dataKey="count" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Text>No data available for Risks by Owner.</Text>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      {/* Row 4: CIA Triad Impact and Threat Level Distribution */}
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            shadow="sm"
            padding="xl"
            radius="md"
            withBorder
            sx={{ height: '100%', border: '1px solid #e3e3e3' }}
          >
            <Title order={3} mb="md" sx={{ fontWeight: 500, color: '#333' }}>
              CIA Triad Impact
            </Title>
            {securityRisksByCIA && securityRisksByCIA.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={securityRisksByCIA}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={false}
                  >
                    {securityRisksByCIA.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={securityRiskColors[entry.name] || '#465362'}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Text>No security risk data available.</Text>
            )}
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            shadow="sm"
            padding="xl"
            radius="md"
            withBorder
            sx={{ height: '100%', border: '1px solid #e3e3e3' }}
          >
            <Title order={3} mb="md" sx={{ fontWeight: 500, color: '#333' }}>
              Risk Priority Distribution
            </Title>
            {riskPriorityDistribution.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={riskPriorityDistribution}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    tickLine={false}
                    axisLine={{ stroke: '#d0d0d0' }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={{ stroke: '#d0d0d0' }}
                  />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar dataKey="value">
                    {riskPriorityDistribution.map((entry, index) => {
                      const priorityColors = {
                        Critical: '#7A1F1F',
                        High: '#993333',
                        Significant: '#C06030',
                        Moderate: '#D4AA7D',
                        Low: '#B8C2B7',
                        Negligible: '#D8D8D8'
                      };
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={priorityColors[entry.name] || '#8884d8'}
                        />
                      );
                    })}
                    <LabelList dataKey="value" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Text>No risk priority data available.</Text>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      {/* Row 5: Assessments by Status and Policy Coverage */}
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            shadow="sm"
            padding="xl"
            radius="md"
            withBorder
            sx={{ height: '100%', border: '1px solid #e3e3e3' }}
          >
            <Title order={3} mb="md" sx={{ fontWeight: 500, color: '#333' }}>
              Assessments by Status
            </Title>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={assessmentStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                  labelLine={false}
                >
                  {assessmentStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            shadow="sm"
            padding="xl"
            radius="md"
            withBorder
            sx={{ height: '100%', border: '1px solid #e3e3e3' }}
          >
            <Title order={3} mb="md" sx={{ fontWeight: 500, color: '#333' }}>
              Policy Coverage
            </Title>
            {policyCoverageData.total > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'With Evidence', value: policyCoverageData.withEvidence },
                      { name: 'Without Evidence', value: policyCoverageData.withoutEvidence }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={false}
                  >
                    <Cell fill="#223B66" />
                    <Cell fill="#D8D8D8" />
                  </Pie>
                  <Tooltip
                    content={({ payload }) => {
                      if (payload && payload.length > 0) {
                        const data = payload[0];
                        return (
                          <Paper p="xs" withBorder>
                            <Text size="sm" fw={500}>{data.name}</Text>
                            <Text size="sm">{data.value} controls</Text>
                          </Paper>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend verticalAlign="bottom" height={36} />
                  <text
                    x="50%"
                    y="45%"
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{ fontSize: '24px', fontWeight: 'bold', fill: '#333' }}
                  >
                    {policyCoverageData.coveragePercentage}%
                  </text>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Text>No policy coverage data available.</Text>
            )}
          </Card>
        </Grid.Col>
      </Grid>
   
      
    {/* Owner Modal */}
    <Modal
      opened={openOwnerModal}
      onClose={handleCloseOwnerModal}
      centered
      size="lg"
      styles={{
        body: {
          backgroundColor: '#fbfbfb',
          maxHeight: '90vh'
        }
      }}
    >
        <Title order={3} id="owner-modal-title" sx={{ fontWeight: 500, color: '#333' }}>
          Risks owned by {selectedOwnerName}
        </Title>
        {Array.isArray(selectedOwnerRisks) && selectedOwnerRisks.length > 0 ? (
          <ul>
            {selectedOwnerRisks.map((riskName, index) => (
              <li key={index}>{riskName}</li>
            ))}
          </ul>
        ) : (
          <Text>No risks available for this owner.</Text>
        )}
    </Modal>
    </Stack>
  </Container>
);

};

export default RiskLandscape;
